import React, { useState, useEffect, useMemo } from "react";
import MetaTags from "react-meta-tags";
import { useSelector } from "react-redux";
import "../featureDesign.scss";
import FeatureKpiHeader from "../FeatureKpiHeader";
import { useRiskTrendHistory, severityCounters } from "../riskTrend";
import FeatureTableCard from "../FeatureTableCard";
import ScanProfileTabPane from "../ScanProfileTabPane";

const WHOisRecords = () => {
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [featureNameID, setfeatureNameID] = useState("");
  const [ScanProfileId, setScanProfileId] = useState("");
  const [Checks, setChecks] = useState([]);
  const [activeTab, setActiveTab] = useState("data");
  const [sectionFilter, setSectionFilter] = useState("ALL");

  const [criticalCardPrice, setCriticalCardPrice] = useState(0);
  const [highCardPrice, setHighCardPrice] = useState(0);
  const [mediumCardPrice, setMediumCardPrice] = useState(0);
  const [lowCardPrice, setLowCardPrice] = useState(0);
  const [informationalCardPrice, setInformationalCardPrice] = useState(0);

  const userID = localStorage.getItem("userID");
  const orgID = localStorage.getItem("orgID");
  const token = localStorage.getItem("token");
  const siteDomain = localStorage.getItem("domain") || "scanme.nmap.org";

  const { layoutMode } = useSelector((state) => ({
    layoutMode: state.Layout.layoutMode,
  }));
  const isDark =
    layoutMode === "dark" ||
    (typeof document !== "undefined" && document.body.getAttribute("data-layout-mode") === "dark");
  const theme = isDark ? "dark" : "light";

  // Real per-day history for the Risk trend chart
  const trendHistory = useRiskTrendHistory(165112129704697, severityCounters.flat, loading);

  useEffect(() => {
    async function checkAuthAndLoad() {
      try {
        if (token) {
          const authRes = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          const authData = await authRes.json();
          if (authData && authData.status === false) {
            window.location.href = "/login";
            return;
          }
        }
      } catch (e) {
        console.warn("Auth check error:", e);
      }
      getWhoIsRecordData();
      getScanProfileString();
    }
    checkAuthAndLoad();
  }, []);

  async function getWhoIsRecordData() {
    setLoading(true);
    const featureIDForServer = 165112129704697;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}taskData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();

      if (data && data.data && data.data.Data && data.data.Data.length > 0) {
        const mainData = data.data.Data[0];
        const rawDataList = data.data.Data;
        let c = 0, h = 0, m = 0, l = 0, inf = 0;

        rawDataList.forEach((item) => {
          const rawRisk = item.Risk || "Low";
          const rLower = String(rawRisk).toLowerCase();
          if (rLower.includes("crit")) c++;
          else if (rLower.includes("high")) h++;
          else if (rLower.includes("med")) m++;
          else if (rLower.includes("info")) inf++;
          else l++;
        });

        const rows = [];
        let rowIdx = 1;

        const addRow = (section, field, value) => {
          if (value && value !== "" && value !== "null" && value !== "undefined") {
            rows.push({
              ID: String(rowIdx++),
              Section: section,
              Field: field,
              Value: String(value),
            });
          }
        };

        // Domain Info
        addRow("Domain", "Domain Name", mainData.Domain);
        addRow("Domain", "Created Date", mainData.Created_On);
        addRow("Domain", "Expiration Date", mainData.Expired_On);
        addRow("Domain", "Updated Date", mainData.Updated_On);

        // Name Servers
        const rawNs = mainData.NameServers || mainData.nameServers || mainData.nameservers;
        if (Array.isArray(rawNs)) {
          rawNs.forEach((ns, idx) => {
            const host = typeof ns === "string" ? ns : (ns?.HostName || ns?.host_name || ns?.name);
            if (host) {
              addRow("Name Server", `Name Server ${idx + 1}`, host);
            }
          });
        } else if (rawNs && typeof rawNs === "object") {
          const hostList = rawNs.hostNames || rawNs.HostName || [];
          if (Array.isArray(hostList)) {
            hostList.forEach((host, idx) => {
              const val = typeof host === "string" ? host : host?.HostName;
              if (val) addRow("Name Server", `Name Server ${idx + 1}`, val);
            });
          }
        }

        // Registrar Details
        if (mainData.Registrar_Details) {
          const reg = mainData.Registrar_Details;
          addRow("Registrar", "Registrar Name", reg.Registrar_Name);
          addRow("Registrar", "Organization", reg.Registrar_Organization);
          addRow("Registrar", "IANA ID", reg.Registrar_IANA_ID);
          addRow("Registrar", "WHOIS Server", reg.Registrar_WHOIS_Server);
          addRow("Registrar", "Referral URL", reg.Registrar_URL);
          addRow("Registrar", "Support Email", reg.Registrar_Email);
          addRow("Registrar", "Support Phone", reg.Registrar_Phone);
        }

        // Registrant Contact
        if (Array.isArray(mainData.Registrant_Contacts) && mainData.Registrant_Contacts[0]) {
          const ct = mainData.Registrant_Contacts[0];
          addRow("Registrant", "Name", ct.Name);
          addRow("Registrant", "Organization", ct.Organization);
          addRow("Registrant", "Email", ct.Email);
          addRow("Registrant", "Phone", ct.Phone);
          addRow("Registrant", "Street", ct.Street);
          addRow("Registrant", "City", ct.City);
          addRow("Registrant", "State / Province", ct.State);
          addRow("Registrant", "Postal Code", ct.Postal_Code);
          addRow("Registrant", "Country", ct.Country);
        }

        // Admin Contact
        if (Array.isArray(mainData.Admin_Contacts) && mainData.Admin_Contacts[0]) {
          const ct = mainData.Admin_Contacts[0];
          addRow("Admin", "Name", ct.Name);
          addRow("Admin", "Organization", ct.Organization);
          addRow("Admin", "Email", ct.Email);
          addRow("Admin", "Phone", ct.Phone);
          addRow("Admin", "Country", ct.Country);
        }

        // Technical Contact
        if (Array.isArray(mainData.Technical_Contacts) && mainData.Technical_Contacts[0]) {
          const ct = mainData.Technical_Contacts[0];
          addRow("Technical", "Name", ct.Name);
          addRow("Technical", "Organization", ct.Organization);
          addRow("Technical", "Email", ct.Email);
          addRow("Technical", "Phone", ct.Phone);
          addRow("Technical", "Country", ct.Country);
        }

        setProductData(rows);
        setCriticalCardPrice(c);
        setHighCardPrice(h);
        setMediumCardPrice(m);
        setLowCardPrice(l);
        setInformationalCardPrice(inf);
      } else {
        setProductData([]);
        setCriticalCardPrice(0);
        setHighCardPrice(0);
        setMediumCardPrice(0);
        setLowCardPrice(0);
        setInformationalCardPrice(0);
      }
    } catch (err) {
      console.error("Error fetching WHOIS data:", err);
      setProductData([]);
    } finally {
      setLoading(false);
    }
  }

  async function getScanProfileString() {
    const featureIDForServer = 165112129704697;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatesubdomaintable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID, orgID, featureIDForServer }),
      });
      const data = await response.json();
      if (data && data.data) {
        setScanProfileId(data.data.ScanProfileID || "");
        setfeatureNameID(data.data.FeatureName || "");
        setChecks(data.data.Checks || []);
      }
    } catch (err) {
      console.warn("ScanProfile fetch error:", err);
    }
  }

  async function updateProfileString(string, id, riskValue, index, stringID, taskType) {
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}updatestring`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stringID,
          string,
          id,
          riskValue,
          index,
          taskType,
        }),
      });
      const data = await response.json();
      if (data.status === "ok") {
        getScanProfileString();
      }
    } catch (e) {
      console.error("Update string error:", e);
    }
  }

  const totalFindings =
    criticalCardPrice + highCardPrice + mediumCardPrice + lowCardPrice + informationalCardPrice;

  const { score, riskLabel } = useMemo(() => {
    if (totalFindings === 0) return { score: 100, riskLabel: "Low" };
    const deduction =
      criticalCardPrice * 12 + highCardPrice * 6 + mediumCardPrice * 2 + lowCardPrice * 1;
    const rawScore = 100 - deduction;

    let calculatedScore = 98.42;
    let label = "Informational";

    if (criticalCardPrice > 0) {
      calculatedScore = Math.max(15, Math.min(39, rawScore));
      label = "Critical";
    } else if (highCardPrice > 0) {
      calculatedScore = Math.max(40, Math.min(59, rawScore));
      label = "High";
    } else if (mediumCardPrice > 0) {
      calculatedScore = Math.max(60, Math.min(84, rawScore));
      label = "Medium";
    } else if (lowCardPrice > 0) {
      calculatedScore = 98.42;
      label = "Low";
    } else {
      calculatedScore = 98.42;
      label = "Low";
    }

    return { score: calculatedScore, riskLabel: label };
  }, [totalFindings, criticalCardPrice, highCardPrice, mediumCardPrice, lowCardPrice]);

  const filteredData =
    sectionFilter === "ALL"
      ? productData
      : productData.filter((item) => item.Section === sectionFilter);

  const getSectionBadgeStyle = (sec) => {
    switch (sec) {
      case "Domain":
        return {
          background: isDark ? "rgba(74, 134, 245, 0.15)" : "rgba(47, 111, 237, 0.10)",
          color: isDark ? "var(--blue-500)" : "var(--blue-600)",
          border: `1px solid ${isDark ? "rgba(74, 134, 245, 0.3)" : "rgba(47, 111, 237, 0.25)"}`,
        };
      case "Name Server":
        return {
          background: isDark ? "rgba(129, 140, 248, 0.15)" : "rgba(79, 70, 229, 0.10)",
          color: isDark ? "var(--indigo-400)" : "var(--indigo-600)",
          border: `1px solid ${isDark ? "rgba(129, 140, 248, 0.3)" : "rgba(79, 70, 229, 0.25)"}`,
        };
      case "Registrar":
        return {
          background: isDark ? "rgba(45, 212, 191, 0.15)" : "rgba(15, 148, 136, 0.10)",
          color: isDark ? "var(--teal-400)" : "var(--teal-600)",
          border: `1px solid ${isDark ? "rgba(45, 212, 191, 0.3)" : "rgba(15, 148, 136, 0.25)"}`,
        };
      case "Registrant":
        return {
          background: isDark ? "rgba(217, 154, 28, 0.15)" : "rgba(168, 106, 5, 0.10)",
          color: isDark ? "var(--amber-500)" : "var(--amber-600)",
          border: `1px solid ${isDark ? "rgba(217, 154, 28, 0.3)" : "rgba(168, 106, 5, 0.25)"}`,
        };
      case "Admin":
        return {
          background: isDark ? "rgba(226, 103, 15, 0.15)" : "rgba(194, 84, 10, 0.10)",
          color: isDark ? "var(--orange-500)" : "var(--orange-600)",
          border: `1px solid ${isDark ? "rgba(226, 103, 15, 0.3)" : "rgba(194, 84, 10, 0.25)"}`,
        };
      case "Technical":
        return {
          background: isDark ? "rgba(134, 149, 171, 0.15)" : "rgba(91, 107, 133, 0.10)",
          color: isDark ? "var(--slate-400)" : "var(--slate-600)",
          border: `1px solid ${isDark ? "rgba(134, 149, 171, 0.3)" : "rgba(91, 107, 133, 0.25)"}`,
        };
      default:
        return {
          background: "var(--slate-800)",
          color: "var(--fg-secondary)",
          border: "1px solid var(--border-default)",
        };
    }
  };

  const columns = [
    { key: "ID", label: "ID", isId: true },
    {
      key: "Section",
      label: "Section",
      render: (val) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "4px",
            fontWeight: 600,
            fontSize: 12,
            ...getSectionBadgeStyle(val),
          }}
        >
          {val}
        </span>
      ),
    },
    { key: "Field", label: "Field" },
    {
      key: "Value",
      label: "Value",
      isMono: true,
      render: (val) => <span style={{ wordBreak: "break-all" }}>{val}</span>,
    },
  ];

  const sectionsList = ["ALL", "Domain", "Name Server", "Registrar", "Registrant", "Admin", "Technical"];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Exposed WHOIS Records | Glogix</title>
      </MetaTags>

      <div className="page-content feature-page-root" data-theme={theme}>
        <div className="feature-container">
          <FeatureKpiHeader
            title="WHOIS Details"
            breadcrumbItem="WHOIS Details"
            siteTitle={siteDomain}
            score={score}
            riskLabel={riskLabel}
            counts={{
              critical: criticalCardPrice,
              high: highCardPrice,
              medium: mediumCardPrice,
              low: lowCardPrice,
              info: informationalCardPrice,
            }}
            loading={loading}
            trendHistory={trendHistory}
          />

          <FeatureTableCard
            tabs={[
              { key: "data", label: "Exposed WHOIS", count: productData.length },
              { key: "profile", label: "Scan Profile" },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            searchPlaceholder="Search"
            columns={columns}
            data={filteredData}
          >
            {activeTab === "data" && (
              <div style={{ display: "flex", gap: 6, padding: "8px 16px 0", flexWrap: "wrap" }}>
                {sectionsList.map((sec) => (
                  <button
                    key={sec}
                    onClick={() => setSectionFilter(sec)}
                    style={{
                      border: "1px solid",
                      borderColor: sectionFilter === sec ? "var(--brand-primary)" : "var(--border-default)",
                      background: sectionFilter === sec ? "rgba(47, 111, 237, 0.15)" : "var(--bg-surface-muted)",
                      color: sectionFilter === sec ? "var(--brand-primary)" : "var(--fg-secondary)",
                      padding: "4px 10px",
                      borderRadius: "var(--radius-md)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {sec}
                  </button>
                ))}
              </div>
            )}

            {activeTab === "profile" && (
              <ScanProfileTabPane
                featureName={featureNameID || "WHOIS Scan Profile"}
                checks={Checks}
                onDelete={(id, keyword) =>
                  updateProfileString(keyword, ScanProfileId, "Low", 0, id, "delete")
                }
                onSave={(id, keyword, risk) =>
                  updateProfileString(keyword, ScanProfileId, risk, 0, id, "updateRisk")
                }
                onAdd={(keyword, risk) =>
                  updateProfileString(keyword, ScanProfileId, risk, 0, null, "add")
                }
              />
            )}
          </FeatureTableCard>
        </div>
      </div>
    </React.Fragment>
  );
};

export default WHOisRecords;
